import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';

/**
 * Floating format toolbar for the inline editor. Appears above a focused
 * <Editable> in admin edit mode and lets a merchant tweak the field's font
 * size, family, colour, weight, style and spacing. Each change is a per-field
 * style override that <Editable> applies live and persists via the section
 * inline-edit save layer (config.inlineStyles.<fieldKey>.<prop>).
 *
 * Rendered in a portal on document.body so it escapes the section's overflow.
 * `onMouseDown preventDefault` on the shell keeps the contentEditable focused
 * while the toolbar is used.
 */

export type StyleOverride = Record<string, string | number | undefined>;

interface Props {
  anchorEl: HTMLElement;
  value: StyleOverride;
  onChange: (patch: StyleOverride) => void;
  onReset: () => void;
  onClose: () => void;
  /** Current fill colour of the nearest chip/button/card box, for the swatch. */
  fillValue?: string;
}

// Web-safe + template-variable families always render; Google families are
// lazy-loaded on selection. Value is a full font-family stack.
const TEMPLATE_FONTS = [
  { label: 'Default', value: '' },
  { label: 'Heading font', value: 'var(--font-heading)' },
  { label: 'Body font', value: 'var(--font-body)' },
];
const GOOGLE_FONTS = [
  'Inter', 'Poppins', 'Montserrat', 'Manrope', 'Space Grotesk', 'Bricolage Grotesque',
  'Playfair Display', 'Cormorant Garamond', 'Lora', 'DM Serif Display', 'Oswald', 'Roboto',
];
const WEBSAFE_FONTS = [
  { label: 'Serif', value: 'Georgia, "Times New Roman", serif' },
  { label: 'Sans', value: 'system-ui, -apple-system, "Segoe UI", sans-serif' },
  { label: 'Mono', value: 'ui-monospace, Menlo, Consolas, monospace' },
];

function ensureGoogleFont(name: string) {
  if (typeof document === 'undefined') return;
  const id = 'qe-font-' + name.replace(/\s+/g, '-').toLowerCase();
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${name.replace(/\s+/g, '+')}:wght@400;500;600;700;800&display=swap`;
  document.head.appendChild(link);
}

/**
 * Lazy-load the webfont behind a persisted fontFamily override so a saved
 * Google family renders for shoppers too (not just the merchant who picked it).
 * Google options are stored as `'Font Name', sans-serif`; template vars and
 * web-safe stacks need no loading.
 */
export function ensureFontFromFamily(family?: string) {
  if (!family) return;
  const m = family.match(/^'([^']+)'/);
  if (m && GOOGLE_FONTS.includes(m[1])) ensureGoogleFont(m[1]);
}

const px = (v: unknown): string => {
  const n = parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? String(n) : '';
};

// Round a px string to a tidy number ("83.98px" -> "84").
const roundPx = (v: unknown): string => {
  const n = parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? String(Math.round(n)) : '';
};

// Computed colour comes back as rgb()/rgba(); the <input type=color> needs hex.
const rgbToHex = (rgb?: string): string => {
  if (!rgb) return '';
  if (/^#[0-9a-f]{6}$/i.test(rgb)) return rgb;
  const m = rgb.match(/rgba?\(([^)]+)\)/);
  if (!m) return '';
  const [r, g, b] = m[1].split(',').map((x) => parseInt(x, 10));
  if ([r, g, b].some((n) => !Number.isFinite(n))) return '';
  return '#' + [r, g, b].map((n) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0')).join('');
};

const primaryFamily = (stack?: string): string =>
  (stack || '').split(',')[0].replace(/["']/g, '').trim();

const btn = (active: boolean): CSSProperties => ({
  width: 30,
  height: 28,
  borderRadius: 6,
  border: '1px solid ' + (active ? '#6d28d9' : '#e5e7eb'),
  background: active ? '#6d28d9' : '#fff',
  color: active ? '#fff' : '#374151',
  fontWeight: 700,
  fontSize: 13,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});
const field: CSSProperties = {
  height: 28,
  borderRadius: 6,
  border: '1px solid #e5e7eb',
  background: '#fff',
  color: '#374151',
  fontSize: 12,
  padding: '0 6px',
  outline: 'none',
};
const label: CSSProperties = { fontSize: 10, color: '#9ca3af', marginRight: 2, userSelect: 'none' };

export function InlineFormatToolbar({ anchorEl, value, onChange, onReset, onClose, fillValue }: Props) {
  const shellRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: -9999, left: -9999 });

  useLayoutEffect(() => {
    const update = () => {
      const r = anchorEl.getBoundingClientRect();
      const shellH = shellRef.current?.offsetHeight ?? 40;
      const shellW = shellRef.current?.offsetWidth ?? 360;
      let top = r.top - shellH - 8;
      if (top < 8) top = r.bottom + 8; // flip below if no room above
      let left = r.left;
      const maxLeft = window.innerWidth - shellW - 8;
      if (left > maxLeft) left = Math.max(8, maxLeft);
      if (left < 8) left = 8;
      setPos({ top, left });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [anchorEl]);

  // Read the field's *current* computed style once on open so the controls show
  // the real current values (autofilled) rather than blanks. `value` is the
  // explicit override; where it's unset we fall back to `current`.
  const [current, setCurrent] = useState<Record<string, string>>({});
  useLayoutEffect(() => {
    const cs = getComputedStyle(anchorEl);
    setCurrent({
      fontSize: cs.fontSize,
      fontFamily: cs.fontFamily,
      color: cs.color,
      fontWeight: cs.fontWeight,
      fontStyle: cs.fontStyle,
      letterSpacing: cs.letterSpacing,
      lineHeight: cs.lineHeight,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorEl]);

  const sizeVal = value.fontSize != null ? px(value.fontSize) : roundPx(current.fontSize);
  const familyVal = String(value.fontFamily ?? '');
  const colorVal = /^#[0-9a-f]{6}$/i.test(String(value.color ?? ''))
    ? String(value.color)
    : rgbToHex(current.color) || '#111111';
  const spacingVal = value.letterSpacing != null
    ? px(value.letterSpacing)
    : (current.letterSpacing && current.letterSpacing !== 'normal' ? roundPx(current.letterSpacing) : '');
  const lineVal = value.lineHeight != null
    ? String(value.lineHeight)
    : (current.lineHeight && current.fontSize && current.lineHeight.endsWith('px')
        ? String(Math.round((parseFloat(current.lineHeight) / parseFloat(current.fontSize)) * 100) / 100)
        : '');
  const curWeight = parseInt(current.fontWeight || '400', 10);
  const isBold = value.fontWeight != null
    ? String(value.fontWeight) === '700' || String(value.fontWeight) === 'bold'
    : Number.isFinite(curWeight) && curWeight >= 600;
  const isItalic = value.fontStyle != null
    ? String(value.fontStyle) === 'italic'
    : current.fontStyle === 'italic';
  const curFamilyName = primaryFamily(current.fontFamily);
  const curAlign = String(value.textAlign ?? current.textAlign ?? 'left');

  // Alignment icon: three bars, aligned to communicate the direction. Works on
  // block-level editables (headings, subtitles, paragraphs).
  const AlignIcon = ({ dir }: { dir: 'left' | 'center' | 'right' }) => (
    <span
      style={{
        display: 'flex', flexDirection: 'column', gap: 2, width: 14,
        alignItems: dir === 'left' ? 'flex-start' : dir === 'right' ? 'flex-end' : 'center',
      }}
    >
      <span style={{ height: 2, width: '100%', background: 'currentColor', borderRadius: 1 }} />
      <span style={{ height: 2, width: '55%', background: 'currentColor', borderRadius: 1 }} />
      <span style={{ height: 2, width: '80%', background: 'currentColor', borderRadius: 1 }} />
    </span>
  );

  const toolbar = (
    <div
      ref={shellRef}
      data-qe-toolbar=""
      // This toolbar is portal'd to <body>, but in the REACT tree it's still a
      // child of the <Editable> — which lives inside the CTA's <Link>. React
      // events bubble through the react tree, not the DOM, so without this a
      // click on a toolbar control bubbles up to the <Link>'s onClick and the
      // router navigates away. Stop React propagation at the toolbar root — in
      // the BUBBLE phase only (a capture-phase stop would eat the click before
      // it reached the toolbar's own buttons, breaking ✕ / Reset / B / I).
      onClick={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
      onMouseDown={(e) => {
        e.stopPropagation();
        // Keep the contentEditable focused when clicking non-focusable controls.
        const t = e.target as HTMLElement;
        if (!['INPUT', 'SELECT', 'TEXTAREA'].includes(t.tagName)) e.preventDefault();
      }}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        zIndex: 2147483000,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 8px',
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: 10,
        boxShadow: '0 8px 28px rgba(0,0,0,0.16)',
        font: '500 12px/1 system-ui, -apple-system, sans-serif',
        color: '#374151',
        maxWidth: '96vw',
        flexWrap: 'nowrap',
        overflowX: 'auto',
      }}
    >
      {/* Font size */}
      <span style={label}>Size</span>
      <input
        type="number"
        min={8}
        max={200}
        value={sizeVal}
        placeholder="—"
        onChange={(e) => onChange({ fontSize: e.target.value ? `${e.target.value}px` : undefined })}
        style={{ ...field, width: 52 }}
        title="Font size (px)"
      />

      {/* Font family */}
      <select
        value={familyVal}
        onChange={(e) => {
          const v = e.target.value;
          const g = GOOGLE_FONTS.find((f) => v.startsWith(`'${f}'`));
          if (g) ensureGoogleFont(g);
          onChange({ fontFamily: v || undefined });
        }}
        style={{ ...field, maxWidth: 140 }}
        title="Font family"
      >
        {/* First option = current (unchanged); shows the real current family. */}
        <option value="">{curFamilyName || 'Font'}</option>
        {TEMPLATE_FONTS.slice(1).map((f) => (
          <option key={f.label} value={f.value}>{f.label}</option>
        ))}
        <optgroup label="Google">
          {GOOGLE_FONTS.map((f) => (
            <option key={f} value={`'${f}', sans-serif`}>{f}</option>
          ))}
        </optgroup>
        <optgroup label="System">
          {WEBSAFE_FONTS.map((f) => (
            <option key={f.label} value={f.value}>{f.label}</option>
          ))}
        </optgroup>
      </select>

      {/* Colour (swatch only — labelled via title) */}
      <label title="Text colour" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
        <input
          type="color"
          value={colorVal}
          onChange={(e) => onChange({ color: e.target.value })}
          style={{ width: 28, height: 26, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', padding: 0, cursor: 'pointer' }}
        />
      </label>

      {/* Bold / Italic */}
      <button
        type="button"
        style={btn(isBold)}
        onClick={() => onChange({ fontWeight: isBold ? undefined : '700' })}
        title="Bold"
      >B</button>
      <button
        type="button"
        style={{ ...btn(isItalic), fontStyle: 'italic', fontWeight: 500 }}
        onClick={() => onChange({ fontStyle: isItalic ? undefined : 'italic' })}
        title="Italic"
      >I</button>

      {/* Alignment (left / center / right; click active to clear) */}
      {(['left', 'center', 'right'] as const).map((dir) => (
        <button
          key={dir}
          type="button"
          style={{ ...btn(curAlign === dir), display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => onChange({ textAlign: curAlign === dir ? undefined : dir })}
          title={`Align ${dir}`}
        >
          <AlignIcon dir={dir} />
        </button>
      ))}

      {/* Letter spacing */}
      <span style={label} title="Letter spacing">LS</span>
      <input
        type="number"
        step={0.1}
        value={spacingVal}
        placeholder="—"
        onChange={(e) => onChange({ letterSpacing: e.target.value ? `${e.target.value}px` : undefined })}
        style={{ ...field, width: 48 }}
        title="Letter spacing (px)"
      />

      {/* Line height */}
      <span style={label} title="Line height">LH</span>
      <input
        type="number"
        step={0.05}
        value={lineVal}
        placeholder="—"
        onChange={(e) => onChange({ lineHeight: e.target.value ? e.target.value : undefined })}
        style={{ ...field, width: 48 }}
        title="Line height"
      />

      {/* Element fill — background of the nearest chip/button/card box that
          wraps this text. Whole-SECTION background lives in the separate
          per-section "Background" pill (SectionBgControl). */}
      <label title="Fill — background of this badge / button / card" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', borderLeft: '1px solid #e5e7eb', paddingLeft: 8 }}>
        <span style={label}>Fill</span>
        <input
          type="color"
          value={rgbToHex(value.backgroundColor != null ? String(value.backgroundColor) : fillValue) || '#ffffff'}
          onChange={(e) => onChange({ backgroundColor: e.target.value })}
          style={{ width: 28, height: 26, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', padding: 0, cursor: 'pointer' }}
        />
        {value.backgroundColor != null && (
          <button
            type="button"
            onClick={() => onChange({ backgroundColor: undefined })}
            title="Clear fill"
            style={{ ...btn(false), width: 'auto', padding: '0 5px', color: '#9ca3af', fontSize: 11 }}
          >
            ✕
          </button>
        )}
      </label>

      {/* Reset + close */}
      <button type="button" style={{ ...btn(false), width: 'auto', padding: '0 8px', fontWeight: 600 }} onClick={onReset} title="Clear formatting">Reset</button>
      <button type="button" style={{ ...btn(false), color: '#9ca3af' }} onClick={onClose} title="Close">✕</button>
    </div>
  );

  return createPortal(toolbar, document.body);
}
